import { useNavigate, useLocation } from "@solidjs/router";
import { Component, Show, createSignal, onCleanup } from "solid-js";
import { logout, useAuth } from "../store/authStore";
import { useTheme } from "../store/themeStore";
import { useBranding } from "./BrandingProvider";
import { ClientSwitcher } from "./ClientSwitcher";
import { NotificationBell } from "./NotificationBell";
import { trace } from "../lib/trace";
import { NavIcon } from "./NavIcon";

export const TopHeader: Component = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { branding: brandConfig } = useBranding();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = createSignal(false);
  const [showAdminMenu, setShowAdminMenu] = createSignal(false);

  // Close dropdown menus on outside click or Escape key
  const handleDocumentClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest(".thdr__menu-wrap")) {
      setShowUserMenu(false);
      setShowAdminMenu(false);
    }
  };
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      setShowUserMenu(false);
      setShowAdminMenu(false);
    }
  };
  document.addEventListener("click", handleDocumentClick);
  document.addEventListener("keydown", handleKeyDown);
  onCleanup(() => {
    document.removeEventListener("click", handleDocumentClick);
    document.removeEventListener("keydown", handleKeyDown);
  });

  const getPageTitle = () => {
    const path = location.pathname;
    const titles: Record<string, string> = {
      "/": "Dashboard",
      "/dashboard": "Dashboard",
      "/fleet": "Proxy Fleet",
      "/fleet/instances": "Proxy Instances",
      "/fleet/provision": "Fleet Provisioning",
      "/fleet/updates": "Update Campaigns",
      "/gateways": "Gateways",
      "/bundles": "Bundles",
      "/credentials": "Credentials",
      "/deployments": "Deployments",
      "/settings": "Settings",
      "/accounts": "Accounts",
      "/clients": "Accounts",
      "/roles": "Roles",
      "/sessions": "Sessions",
      "/diagnostics": "Diagnostics",
      "/notifications": "Notifications",
      "/health": "Platform Health",
      "/observability": "Observability",
      "/audit": "Platform Audit",
      "/admin/dashboard": "Admin Dashboard",
      "/admin/reconciliation": "Reconciliation",
      "/components": "Component Versions",
      "/onboarding": "Onboarding",
      "/getting-started": "Getting Started",
      "/local/localstack": "LocalStack",
      "/local/azurite": "Azurite",
      "/local/tunnels": "Tunnels",
      "/local/secrets": "Provider Secrets",
      "/local/resources": "Cloud Resources",
      "/local/devcontainer": "Dev Container",
    };
    // Check exact match first
    if (titles[path]) return titles[path];
    // Check prefix match for detail pages (longer prefixes first for correct matching)
    const sortedPrefixes = Object.keys(titles).sort((a, b) => b.length - a.length);
    for (const prefix of sortedPrefixes) {
      if (path.startsWith(prefix + "/")) return titles[prefix];
    }
    return brandConfig().brandName || "AirBrx";
  };

  const handleLogout = async () => {
    trace.auth("Logout initiated from TopHeader");
    await logout();
    trace.auth("Logout completed");
    navigate("/", { replace: true });
  };

  const getUserInitials = () => {
    const name = user()?.name || user()?.email || "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <header class="thdr">
      {/* Search / Breadcrumbs */}
      <div class="thdr__left">
        <h1 class="thdr__title">
          {getPageTitle()}
        </h1>
      </div>

      {/* Right Section */}
      <div class="thdr__right">
        {/* Client Switcher */}
        <Show when={isAuthenticated()}>
          <ClientSwitcher />
        </Show>

        {/* Notification Bell */}
        <Show when={isAuthenticated()}>
          <NotificationBell />
        </Show>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          class="thdr__theme-btn"
          title={`Switch to ${theme() === "light" ? "dark" : "light"} mode`}
          aria-label={`Switch to ${theme() === "light" ? "dark" : "light"} mode`}>
          <NavIcon name={theme() === "light" ? "moon" : "sun"} size={18} />
        </button>

        {/* Admin Menu (only for admins) */}
        <Show when={isAdmin()}>
          <div class="thdr__menu-wrap">
            <button
              onClick={() => setShowAdminMenu(!showAdminMenu())}
              aria-label="Admin menu"
              aria-expanded={showAdminMenu()}
              aria-haspopup="true"
              class="thdr__admin-btn">
              <NavIcon name="settings" size={16} />
              <span>Admin</span>
              <span class="thdr__caret">{showAdminMenu() ? "\u25B2" : "\u25BC"}</span>
            </button>

            <Show when={showAdminMenu()}>
              <div class="thdr__dropdown thdr__dropdown--admin">
                <button
                  onClick={() => {
                    navigate("/clients");
                    setShowAdminMenu(false);
                  }}
                  class="thdr__dropdown-item">
                  <NavIcon name="users" size={16} />
                  <span>Manage Clients</span>
                </button>
                <button
                  onClick={() => {
                    navigate("/deployments");
                    setShowAdminMenu(false);
                  }}
                  class="thdr__dropdown-item">
                  <NavIcon name="deployments" size={16} />
                  <span>All Deployments</span>
                </button>
                <button
                  onClick={() => {
                    navigate("/settings");
                    setShowAdminMenu(false);
                  }}
                  class="thdr__dropdown-item">
                  <NavIcon name="settings" size={16} />
                  <span>System Settings</span>
                </button>
              </div>
            </Show>
          </div>
        </Show>

        {/* User Profile Menu */}
        <Show when={isAuthenticated()}>
          <div class="thdr__menu-wrap">
            <button
              onClick={() => setShowUserMenu(!showUserMenu())}
              aria-label="User menu"
              aria-expanded={showUserMenu()}
              aria-haspopup="true"
              class="thdr__user-btn">
              <div class="thdr__avatar">
                {getUserInitials()}
              </div>
              <div class="thdr__user-info">
                <div class="thdr__user-name">
                  {user()?.name || user()?.email?.split("@")[0]}
                </div>
                <Show when={isAdmin()}>
                  <div class="thdr__user-role">Admin</div>
                </Show>
              </div>
              <span class="thdr__caret thdr__caret--secondary">
                {showUserMenu() ? "\u25B2" : "\u25BC"}
              </span>
            </button>

            <Show when={showUserMenu()}>
              <div class="thdr__dropdown thdr__dropdown--user">
                <div class="thdr__dropdown-header">
                  <div class="thdr__dropdown-header-name">
                    {user()?.name || "User"}
                  </div>
                  <div class="thdr__dropdown-header-email">
                    {user()?.email}
                  </div>
                </div>
                <button
                  onClick={() => {
                    navigate("/settings");
                    setShowUserMenu(false);
                  }}
                  class="thdr__dropdown-item thdr__dropdown-item--first">
                  <NavIcon name="settings" size={16} />
                  <span>Account Settings</span>
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setShowUserMenu(false);
                  }}
                  class="thdr__dropdown-item thdr__dropdown-item--danger">
                  <NavIcon name="logout" size={16} />
                  <span>Log Out</span>
                </button>
              </div>
            </Show>
          </div>
        </Show>
      </div>
    </header>
  );
};
